/**
 * Console information architecture: the primary navigation, the tabs inside each section, and the
 * redirects that keep every earlier URL working.
 *
 * Permissions are the server's (apps/server/src/auth/rbac.ts). The console only uses them to decide
 * what to show: a hidden page visited directly still renders, and the server's 403 shows as
 * "Your role does not allow this."
 */

export type Permission = 'read' | 'audit:read';

export interface NavItem {
  to: string;
  label: string;
  /** Permission needed to view the page (the GET route behind it). */
  perm: Permission;
  /** Path prefixes that mark this item as current (defaults to `to`). */
  match?: string[];
}

export const NAV: Array<{ group: string; items: NavItem[] }> = [
  { group: 'Overview', items: [{ to: '/', label: 'Dashboard', perm: 'read' }] },
  {
    group: 'Investigate',
    items: [
      { to: '/investigate/alerts', label: 'Alerts', perm: 'read', match: ['/investigate/alerts', '/investigate/investigations'] },
      { to: '/investigate/activity', label: 'Activity', perm: 'read' },
    ],
  },
  {
    group: 'Inventory',
    items: [
      { to: '/discover/devices', label: 'Devices', perm: 'read' },
      { to: '/discover/users', label: 'People', perm: 'read', match: ['/discover/users', '/discover/groups'] },
      { to: '/discover/apps', label: 'Applications', perm: 'read' },
      { to: '/discover/extensions', label: 'Extensions', perm: 'read' },
    ],
  },
  {
    group: 'Protect',
    items: [
      { to: '/protect/policies', label: 'Policies', perm: 'read', match: ['/protect/policies', '/protect/exceptions', '/protect/classifications'] },
      { to: '/protect/threats/web', label: 'Threat protection', perm: 'read', match: ['/protect/threats'] },
    ],
  },
  {
    group: 'Admin',
    items: [
      { to: '/admin/deployment', label: 'Deployment', perm: 'read' },
      { to: '/admin/integrations/microsoft', label: 'Integrations', perm: 'read', match: ['/admin/integrations'] },
      { to: '/admin/settings', label: 'Settings', perm: 'read' },
      { to: '/admin/audit', label: 'Audit log', perm: 'audit:read' },
    ],
  },
];

/** The navigation a role sees: items it may view, and no empty groups. */
export function visibleNav(can: (p: string) => boolean) {
  return NAV.map((g) => ({ ...g, items: g.items.filter((i) => can(i.perm)) })).filter((g) => g.items.length > 0);
}

const under = (pathname: string, prefix: string) => (prefix === '/' ? pathname === '/' : pathname === prefix || pathname.startsWith(`${prefix}/`));

export const isCurrent = (item: NavItem, pathname: string) => (item.match ?? [item.to]).some((p) => under(pathname, p));

export interface Tab { to: string; label: string; perm?: Permission; end?: boolean }

export const TABS = {
  alerts: [
    { to: '/investigate/alerts', label: 'Alerts', end: true },
    { to: '/investigate/investigations', label: 'Investigations', end: true },
  ],
  people: [
    { to: '/discover/users', label: 'Users', end: true },
    { to: '/discover/groups', label: 'Groups', end: true },
  ],
  extensions: [
    { to: '/discover/extensions', label: 'Inventory', end: true },
    { to: '/discover/extensions/rules', label: 'Rules', end: true },
  ],
  policies: [
    { to: '/protect/policies', label: 'Policies', end: true },
    { to: '/protect/exceptions', label: 'Exceptions', end: true },
    { to: '/protect/classifications', label: 'Data classifications', end: true },
  ],
  threats: [
    { to: '/protect/threats/web', label: 'Web', end: true },
    { to: '/protect/threats/downloads', label: 'Downloads', end: true },
    { to: '/protect/threats/privacy', label: 'Privacy', end: true },
    { to: '/protect/threats/intel', label: 'Threat intel', end: true },
  ],
  integrations: [
    { to: '/admin/integrations/microsoft', label: 'Microsoft', end: true },
    { to: '/admin/integrations/webhooks', label: 'SIEM / Webhooks', end: true },
  ],
  settings: [
    { to: '/admin/settings', label: 'General', end: true },
    { to: '/admin/settings/admins', label: 'Administrators', end: true },
    { to: '/admin/settings/diagnostics', label: 'Diagnostics', perm: 'audit:read', end: true },
  ],
} satisfies Record<string, Tab[]>;

/**
 * Earlier URLs → current URLs. `from` is a path prefix: anything after it (an id, `/new`, `/lists/:id`)
 * is carried over, and the query string and hash are kept. Query parameters in `to` are added unless
 * the old URL already sets them.
 */
export const REDIRECTS: Array<[from: string, to: string]> = [
  // Protect: threat pages moved under Threat protection; the AI / Data / Identity lenses became the
  // Policies area filter; the extension ledger and rules moved to Inventory → Extensions.
  ['/protect/web', '/protect/threats/web'],
  ['/protect/downloads', '/protect/threats/downloads'],
  ['/protect/privacy', '/protect/threats/privacy'],
  ['/protect/threat-intel', '/protect/threats/intel'],
  ['/protect/ai', '/protect/policies?area=ai'],
  ['/protect/data', '/protect/policies?area=data'],
  ['/protect/identity', '/protect/policies?area=identity'],
  ['/protect/extensions/rules', '/discover/extensions/rules'],
  ['/protect/extensions', '/discover/extensions'],
  ['/discover/ai', '/discover/apps?view=ai'],
  // Manage → Protect / Inventory.
  ['/manage/policies', '/protect/policies'],
  ['/manage/exceptions', '/protect/exceptions'],
  ['/manage/classifications', '/protect/classifications'],
  ['/manage/groups', '/discover/groups'],
  // Integrations and System → Admin.
  ['/integrations/microsoft', '/admin/integrations/microsoft'],
  ['/integrations/webhooks', '/admin/integrations/webhooks'],
  ['/system/deployment', '/admin/deployment'],
  ['/system/audit', '/admin/audit'],
  ['/system/settings', '/admin/settings'],
];

/** Section roots without a page of their own. Exact match only. */
export const SECTION_ROOTS: Array<[from: string, to: string]> = [
  ['/investigate', '/investigate/alerts'],
  ['/discover', '/discover/apps'],
  ['/protect', '/protect/policies'],
  ['/protect/threats', '/protect/threats/web'],
  ['/manage', '/protect/policies'],
  ['/integrations', '/admin/integrations/microsoft'],
  ['/admin', '/admin/deployment'],
  ['/admin/integrations', '/admin/integrations/microsoft'],
  ['/system', '/admin/settings'],
];

/** Where an old URL now lives. `from`/`to` as in REDIRECTS; `search` includes its leading `?`. */
export function movedTo(from: string, to: string, pathname: string, search = '', hash = ''): string {
  const [toPath, toQuery = ''] = to.split('?') as [string, string?];
  const rest = pathname.startsWith(from) ? pathname.slice(from.length) : '';
  const qs = new URLSearchParams(toQuery);
  const fixed = new Set(qs.keys());
  new URLSearchParams(search).forEach((v, k) => { if (!fixed.has(k)) qs.append(k, v); });
  const s = qs.toString();
  return `${toPath}${rest}${s ? `?${s}` : ''}${hash}`;
}
